import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

const CategoryMenu = ({ categoriesTree, loadingTree }) => {
  const [selectedMainCategory, setSelectedMainCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);

  // Helper function to get the category ID
  const getCategoryId = (category) => {
    return category.categoryId ?? category.id ?? category.category_id ?? category.slug ?? category.name;
  };

  // Helper function to get the category name
  const getCategoryName = (category) => {
    return category.categoryName ?? category.name ?? category.title ?? 'Category';
  };

  // Helper function to get children
  const getChildren = (category) => {
    return category.children ?? category.subCategories ?? category.childCategories ?? category.subcategories ?? [];
  };

  // Helper function to check if category has children
  const hasChildren = (category) => {
    const children = getChildren(category);
    return children && children.length > 0;
  };

  if (loadingTree) {
    return (
      <div className="text-sm text-gray-500 text-center py-4">
        Loading categories...
      </div>
    );
  }

  if (!categoriesTree || categoriesTree.length === 0) {
    return (
      <div className="text-sm text-gray-500 text-center py-4">
        No categories available.
      </div>
    );
  }

  const mainCategory = selectedMainCategory 
    ? categoriesTree.find(cat => getCategoryId(cat) === selectedMainCategory)
    : null;

  const subCategory = mainCategory && selectedSubCategory
    ? getChildren(mainCategory).find(cat => getCategoryId(cat) === selectedSubCategory)
    : null;

  const mainCategoryChildren = mainCategory ? getChildren(mainCategory) : [];
  const subCategoryChildren = subCategory ? getChildren(subCategory) : [];

  return (
    <div className="flex h-[500px]">
      {/* Left Column - Main Categories */}
      <div className="w-64 border-r border-gray-200 overflow-y-auto">
        <div className="py-2">
          {categoriesTree.map((category) => {
            const categoryId = getCategoryId(category);
            const categoryName = getCategoryName(category);
            const hasSubCategories = hasChildren(category);
            const isSelected = selectedMainCategory === categoryId;

            return (
              <div
                key={categoryId}
                onMouseEnter={() => {
                  setSelectedMainCategory(categoryId);
                  setSelectedSubCategory(null); // Reset sub-category selection
                }}
                className={`px-4 py-2.5 cursor-pointer transition-colors ${
                  isSelected
                    ? 'bg-gray-50 border-l-4 border-red-600'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium ${
                    isSelected ? 'text-gray-900' : 'text-gray-700'
                  }`}>
                    {categoryName}
                  </span>
                  {hasSubCategories && (
                    <ChevronRight 
                      size={16} 
                      className={`text-gray-400 ${
                        isSelected ? 'text-gray-600' : ''
                      }`} 
                    />
                  )}
                </div>
                {isSelected && (
                  <div className="h-0.5 bg-red-600 mt-1.5 -ml-4"></div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Middle Column - Sub Categories */}
      {mainCategory && mainCategoryChildren.length > 0 && (
        <div className="w-64 border-r border-gray-200 overflow-y-auto">
          <div className="py-2">
            {/* Shop All Link */}
            <Link
              to={`/products?category=${getCategoryId(mainCategory)}`}
              className="block px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-gray-50 transition-colors"
              onClick={() => {
                setSelectedMainCategory(null);
                setSelectedSubCategory(null);
              }}
            >
              Shop All {getCategoryName(mainCategory)} →
            </Link>

            {/* Sub Categories */}
            {mainCategoryChildren.map((subCategory) => {
              const subCategoryId = getCategoryId(subCategory);
              const subCategoryName = getCategoryName(subCategory);
              const hasSubSubCategories = hasChildren(subCategory);
              const isSelected = selectedSubCategory === subCategoryId;

              return (
                <div
                  key={subCategoryId}
                  onMouseEnter={() => {
                    if (hasSubSubCategories) {
                      setSelectedSubCategory(subCategoryId);
                    }
                  }}
                  className={`px-4 py-2.5 cursor-pointer transition-colors ${
                    isSelected
                      ? 'bg-gray-100 border-l-4 border-red-600'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    {hasSubSubCategories ? (
                      <span className={`text-sm font-medium ${
                        isSelected ? 'text-gray-900' : 'text-gray-700'
                      }`}>
                        {subCategoryName}
                      </span>
                    ) : (
                      <Link
                        to={`/products?category=${subCategoryId}`}
                        className={`text-sm font-medium ${
                          isSelected ? 'text-gray-900' : 'text-gray-700'
                        } hover:text-red-600 transition-colors`}
                        onClick={() => {
                          setSelectedMainCategory(null);
                          setSelectedSubCategory(null);
                        }}
                      >
                        {subCategoryName}
                      </Link>
                    )}
                    {hasSubSubCategories && (
                      <ChevronRight 
                        size={16} 
                        className={`text-gray-400 ${
                          isSelected ? 'text-gray-600' : ''
                        }`} 
                      />
                    )}
                  </div>
                  {isSelected && (
                    <div className="h-0.5 bg-red-600 mt-1.5 -ml-4"></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Right Column - Sub Sub Categories */}
      {subCategory && subCategoryChildren.length > 0 && (
        <div className="flex-1 overflow-y-auto">
          <div className="py-2">
            {/* Shop All Link */}
            <Link
              to={`/products?category=${getCategoryId(subCategory)}`}
              className="block px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-gray-50 transition-colors"
              onClick={() => {
                setSelectedMainCategory(null);
                setSelectedSubCategory(null);
              }}
            >
              Shop All {getCategoryName(subCategory)} →
            </Link>

            {/* Sub Sub Categories */}
            <div className="px-4 py-2">
              {subCategoryChildren.map((subSubCategory) => {
                const subSubCategoryId = getCategoryId(subSubCategory);
                const subSubCategoryName = getCategoryName(subSubCategory);

                return (
                  <Link
                    key={subSubCategoryId}
                    to={`/products?category=${subSubCategoryId}`}
                    className="block py-2 text-sm text-gray-700 hover:text-red-600 hover:bg-gray-50 transition-colors rounded px-2"
                    onClick={() => {
                      setSelectedMainCategory(null);
                      setSelectedSubCategory(null);
                    }}
                  >
                    {subSubCategoryName}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Empty State - Show message when no category is selected */}
      {!mainCategory && (
        <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
          Hover over a category to see subcategories
        </div>
      )}
    </div>
  );
};

export default CategoryMenu;
